import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Stepper } from "@mantine/core";
import { gql } from "@/__generated__";
import { Button } from "@/components/buttons/Button/Button";
import styles from "./refer-patient-progress-tracker.module.scss";

export const GET_APPEAL_FOR_PROGRESS_TRACKER = gql(`
  query GetAppealForProgressTracker($appealId: ID!) {
    appealStatus(appealId: $appealId) {
      referred
    }
    appealPatient(appealId: $appealId) {
      firstName
      lastName
      dateOfBirth
    }
  }
`);

export const SEND_REFERRAL_TO_PATIENT = gql(`
  mutation SendReferralToPatient($appealId: ID!) {
    sendReferralToPatient(appealId: $appealId)
  }
`);

interface ReferPatientProgressTrackerProps {
  appealId?: string;
  onBeforeSendReferral?: () => Promise<boolean>;
}

function getActiveStep(pathname: string): number {
  if (pathname.startsWith("/appeals/") && pathname.includes("/strengthen")) {
    return 2;
  }

  if (pathname.startsWith("/refer/") && pathname.endsWith("/patient-info")) {
    return 1;
  }

  if (pathname === "/refer") {
    return 0;
  }

  return 0;
}

export function ReferPatientProgressTracker({
  appealId,
  onBeforeSendReferral,
}: ReferPatientProgressTrackerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeStep = getActiveStep(location.pathname);

  const { data, loading } = useQuery(GET_APPEAL_FOR_PROGRESS_TRACKER, {
    variables: { appealId: appealId || "" },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const [sendReferralToPatient, { loading: mutationLoading }] = useMutation(
    SEND_REFERRAL_TO_PATIENT,
  );

  if (appealId && loading) {
    return null;
  }

  const patient = data?.appealPatient;
  const hasPatientInfo = !!(
    patient?.firstName &&
    patient?.lastName &&
    patient?.dateOfBirth
  );
  const isReferred = data?.appealStatus?.referred || false;

  const shouldShowSendButton = appealId && hasPatientInfo && !isReferred;
  const isSendButtonDisabled = loading || mutationLoading;

  const handleSendReferral = async () => {
    if (!appealId) return;

    if (onBeforeSendReferral) {
      const canProceed = await onBeforeSendReferral();
      if (!canProceed) {
        return;
      }
    }

    await sendReferralToPatient({
      variables: { appealId },
    });
    navigate("/", {
      state: {
        success: true,
      },
    });
  };

  const StepIcon = ({ step }: { step: number }) => <span>{step}</span>;

  const title = isReferred ? "Appeal detail" : "Refer patient";

  return (
    <div className={styles.container}>
      <div className={styles.leftSection}>
        <div className={styles.iconContainer} />
        <h1 className={styles.title}>{title}</h1>
        {!isReferred && (
          <Stepper
            active={activeStep}
            orientation="horizontal"
            classNames={{
              root: styles.stepper,
              step: styles.step,
              stepIcon: styles.stepIcon,
              stepLabel: styles.stepLabel,
              separator: styles.separator,
            }}
            iconSize={32}
          >
            <Stepper.Step
              label="Denial Info"
              icon={<StepIcon step={1} />}
              completedIcon={<StepIcon step={1} />}
            />
            <Stepper.Step
              label="Patient Info"
              icon={<StepIcon step={2} />}
              completedIcon={<StepIcon step={2} />}
            />
            <Stepper.Step
              label="Strengthen Referral"
              icon={<StepIcon step={3} />}
              completedIcon={<StepIcon step={3} />}
            />
          </Stepper>
        )}
      </div>

      {shouldShowSendButton && (
        <div className={styles.buttonContainer}>
          <Button
            variant="primary"
            size="small"
            onClick={handleSendReferral}
            disabled={isSendButtonDisabled}
            loading={mutationLoading}
          >
            Send Referral
          </Button>
        </div>
      )}
    </div>
  );
}
